/**
 * @class meleCode
 * @description
 * `<mele-code>` — code viewer/editor wrapping CodeMirror 6.
 * Supports JSON and XML with syntax highlighting, folding, search and lint.
 * Language can be set explicitly or auto-detected from the first non-whitespace
 * character (< → xml, { or [ → json, otherwise plain text).
 *
 * Properties:
 *   value      string  the text content
 *   syntax   string  'auto' | 'json' | 'xml' | 'plain' (default 'auto')
 *   readonly   boolean disables editing but keeps fold/search/navigation
 *
 * Event:
 *   eventValueChanged(self, newValue) — fires on user-driven document changes
 */

import meleElement from 'meleElement'
import { EditorView, basicSetup } from 'codemirror'
import { EditorState, Compartment } from '@codemirror/state'
import { json, jsonParseLinter } from '@codemirror/lang-json'
import { xml } from '@codemirror/lang-xml'
import { linter, lintGutter } from '@codemirror/lint'

const PROPERTIES = _.extend({}, {
  value: { type: 'string', description: 'Text content' },
  syntax: { type: 'string', description: 'Language mode', enum: ['auto', 'json', 'xml', 'plain'], default: 'auto' },
  readonly: { type: 'boolean', description: 'Disable editing', default: false },
  eventValueChanged: { type: 'function', description: 'Fired on user document changes' },
}, meleElement._properties)

function detectLanguage(value) {
  const trimmed = String(value || '').replace(/^\s+/, '')
  if(!trimmed) return 'plain'
  const first = trimmed[0]
  if(first === '<') return 'xml'
  if(first === '{' || first === '[') return 'json'
  return 'plain'
}

function tryFormatJson(text) {
  try { return JSON.stringify(JSON.parse(text), null, 2) } catch { return text }
}

// basic indent-based XML pretty-printer; preserves invalid input by returning it untouched
function tryFormatXml(text) {
  try {
    if(!text.trim()) return text
    let xml = text.replace(/>\s+</g, '><').trim()
    xml = xml.replace(/(>)(<)(\/*)/g, '$1\n$2$3')
    let formatted = ''
    let pad = 0
    xml.split('\n').forEach(line => {
      line = line.trim()
      if(!line) return
      let nextPad = 0
      if(/^<\/\w/.test(line)) {
        pad = Math.max(pad - 1, 0)
      } else if(/^<[^?!\/][^>]*[^\/]>$/.test(line)) {
        nextPad = 1
      }
      formatted += '  '.repeat(pad) + line + '\n'
      pad += nextPad
    })
    return formatted.trimEnd()
  } catch { return text }
}

function tryFormat(text, lang) {
  if(lang === 'json') return tryFormatJson(text)
  if(lang === 'xml') return tryFormatXml(text)
  return text
}

function xmlLinter() {
  return linter(view => {
    const text = view.state.doc.toString()
    if(!text.trim()) return []
    try {
      const doc = new DOMParser().parseFromString(text, 'application/xml')
      const errorNode = doc.querySelector('parsererror')
      if(errorNode) {
        return [{
          from: 0,
          to: Math.min(text.length, 1),
          severity: 'error',
          message: (errorNode.textContent || 'XML parse error').replace(/\s+/g, ' ').trim(),
        }]
      }
    } catch(e) {
      return [{ from: 0, to: 1, severity: 'error', message: String(e?.message || e) }]
    }
    return []
  })
}

export default class meleCode extends meleElement {
  init() {
    this._body = this.appendChildren({ tag: 'div', class: 'container' })
    setTimeout(() => this._renderEditor(), 0)
  }

  _detectedLanguage() {
    if(this.syntax && this.syntax !== 'auto') return this.syntax
    return detectLanguage(this.value)
  }

  _syntaxExtensions(lang) {
    if(lang === 'json') return [json(), linter(jsonParseLinter())]
    if(lang === 'xml') return [xml(), xmlLinter()]
    return []
  }

  _renderEditor() {
    const me = this
    this._syntaxCompartment = new Compartment()
    this._readonlyCompartment = new Compartment()
    this._internalDispatch = false

    const lang = this._detectedLanguage()
    const initial = tryFormat(String(this.value || ''), lang)

    const state = EditorState.create({
      doc: initial,
      extensions: [
        basicSetup,
        lintGutter(),
        this._syntaxCompartment.of(this._syntaxExtensions(lang)),
        this._readonlyCompartment.of(EditorState.readOnly.of(!!this.readonly)),
      ],
    })

    this._view = new EditorView({
      state,
      parent: this._body,
      // intercept dispatch so we can reliably notify on user-driven changes.
      // _internalDispatch is set when WE replace the doc (initial set / formatting / external value change)
      // so we don't fire eventValueChanged and mark the form dirty for non-user edits.
      dispatch: tr => {
        me._view.update([tr])
        if(tr.docChanged && !me._internalDispatch) {
          const val = me._view.state.doc.toString()
          if(typeof me.eventValueChanged === 'function') me.eventValueChanged(me, val)
          me.dispatchEvent(new CustomEvent('input', { bubbles: true, detail: { value: val } }))
        }
      },
    })
  }

  valueChanged(value) {
    if(!this._view) return
    const lang = this._detectedLanguage(value)
    const incoming = tryFormat(String(value || ''), lang)
    if(this._view.state.doc.toString() === incoming) return
    this._internalDispatch = true
    try {
      this._view.dispatch({
        changes: { from: 0, to: this._view.state.doc.length, insert: incoming },
      })
      if(!this.syntax || this.syntax === 'auto') {
        this._view.dispatch({
          effects: this._syntaxCompartment.reconfigure(this._syntaxExtensions(lang)),
        })
      }
    } finally {
      this._internalDispatch = false
    }
  }

  syntaxChanged() {
    if(!this._view) return
    this._view.dispatch({
      effects: this._syntaxCompartment.reconfigure(this._syntaxExtensions(this._detectedLanguage())),
    })
  }

  readonlyChanged() {
    if(!this._view) return
    this._view.dispatch({
      effects: this._readonlyCompartment.reconfigure(EditorState.readOnly.of(!!this.readonly)),
    })
  }

  // Synchronous "is current document well-formed?" check.
  // We don't go through CodeMirror's lint state because that updates via a separate
  // (async) dispatch — by the time we'd read it, we'd be one keystroke behind.
  // Same parsers as the lint extensions; we just run them against the live doc.
  isValid() {
    if(!this._view) return true
    const text = this._view.state.doc.toString()
    if(!text.trim()) return true
    const lang = this._detectedLanguage()
    if(lang === 'json') {
      try { JSON.parse(text); return true } catch { return false }
    }
    if(lang === 'xml') {
      try {
        const doc = new DOMParser().parseFromString(text, 'application/xml')
        return !doc.querySelector('parsererror')
      } catch { return false }
    }
    return true
  }
}

meleCode._properties = PROPERTIES
meleCode.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-code', meleCode)
